import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AiFillEye, AiFillGithub } from 'react-icons/ai';
import { motion, AnimatePresence } from 'framer-motion';

import { AppWrap, MotionWrap } from '../../wrapper';
import { urlFor, client } from '../../client';
import { useTheme } from '../../context/ThemeContext';
import './Work.scss';

const Work = () => {
  const navigate = useNavigate();
  const [works, setWorks] = useState([]);
  const [filterWork, setFilterWork] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All');
  const [animateCard, setAnimateCard] = useState({ y: 0, opacity: 1 });
  const { isDarkMode } = useTheme();

  useEffect(() => {
    // Fetch both works and blockchain projects
    const worksQuery = '*[_type == "works"] | order(order asc)';
    const blockchainQuery = '*[_type == "blockchainProjectDetails" && isVisible != false] | order(_createdAt desc)';

    Promise.all([
      client.fetch(worksQuery),
      client.fetch(blockchainQuery)
    ]).then(([worksData, blockchainData]) => {
      // Transform blockchain projects to match works structure for display
      const transformedBlockchain = blockchainData.map((project) => ({
        ...project,
        title: project.title,
        description: project.shortPitch || project.description || '',
        imgUrl: project.heroImage,
        projectLink: project.liveLink || '#',
        codeLink: project.githubLink || '#',
        tags: project.tags || [project.category || 'Blockchain'],
        isBlockchain: true,
      }));

      // Combine all projects
      const allProjects = [...worksData, ...transformedBlockchain];
      setWorks(allProjects);
      setFilterWork(allProjects);
    }).catch((err) => {
      console.error('Error fetching projects:', err);
      // Fallback to just works if blockchain fetch fails
      client.fetch(worksQuery).then((data) => {
        setWorks(data);
        setFilterWork(data);
      });
    });
  }, []);

  const handleWorkFilter = (item) => {
    setActiveFilter(item);
    setAnimateCard({ y: 100, opacity: 0 });

    setTimeout(() => {
      setAnimateCard({ y: 0, opacity: 1 });

      if (item === 'All') {
        setFilterWork(works);
      } else {
        setFilterWork(works.filter((work) => work.tags.includes(item)));
      }
    }, 500);
  };

  return (
    <div className={isDarkMode ? 'dark-mode' : ''}>
      <h2 className="head-text">My Creative <span>Portfolio</span> Section</h2>

      <div className="app__work-filter">
        {['Web3', 'Web App', 'React JS', 'All'].map((item, index) => (
          <motion.div
            key={index}
            onClick={() => handleWorkFilter(item)}
            className={`app__work-filter-item app__flex p-text ${activeFilter === item ? 'item-active' : ''}`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {item}
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        <motion.div
          animate={animateCard}
          transition={{ duration: 0.5, delayChildren: 0.5 }}
          className="app__work-portfolio"
        >
          {filterWork.map((work, index) => (
            <motion.div
              className="app__work-item app__flex"
              key={work._id || index}
              whileHover={{ y: -10 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div 
                className="app__work-img app__flex"
                onClick={() => navigate(`/work/${work._id}`)}
                style={{ cursor: 'pointer' }}
              >
                <img src={urlFor(work.imgUrl)} alt={work.name} />

                <motion.div
                  whileHover={{ opacity: [0, 1] }}
                  transition={{ duration: 0.25, ease: 'easeInOut', staggerChildren: 0.5 }}
                  className="app__work-hover app__flex"
                >
                  <a href={work.projectLink} target="_blank" rel="noreferrer">
                    <motion.div
                      whileInView={{ scale: [0, 1] }}
                      whileHover={{ scale: [1, 0.9] }}
                      transition={{ duration: 0.25 }}
                      className="app__flex"
                    >
                      <AiFillEye />
                    </motion.div>
                  </a>
                  <a href={work.codeLink} target="_blank" rel="noreferrer">
                    <motion.div
                      whileInView={{ scale: [0, 1] }}
                      whileHover={{ scale: [1, 0.9] }}
                      transition={{ duration: 0.25 }}
                      className="app__flex"
                    >
                      <AiFillGithub />
                    </motion.div>
                  </a>
                </motion.div>
              </div>

              <div className="app__work-content app__flex">
                <h4 className="bold-text">{work.title}</h4>
                <motion.p 
                  className="p-text" 
                  style={{ marginTop: 10, cursor: 'pointer' }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  onClick={() => navigate(`/work/${work._id}`)}
                >
                  {work.description.split(' ').slice(0, 35).join(' ')}
                  {work.description.split(' ').length > 35 ? '...' : ''}
                </motion.p>

                <div className="app__work-tag app__flex">
                  <p className="p-text">{work.tags[0]}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default AppWrap(
  MotionWrap(Work, 'app__works'),
  'work',
  'app__primarybg',
);